import { Info } from "lucide-react";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

const TIPS = {
  todo: "Prospects ouverts avec au moins une action visible dont l'échéance est aujourd'hui ou dépassée.",
  no_action: "Aucune tâche ouverte dans votre périmètre de tâches (Nouveau lead → Démo / Négo et Gagné).",
  dormant: "Aucune activité depuis plus de 14 jours (clients actifs et perdus exclus) — même règle que le tableau de bord.",
  unassigned: "Prospects sans responsable : identique au périmètre « Non assignés ».",
};

// The four action counters of Phase x Scope: exact numbers from the server, `…` while they load, an honest message when they fail,
// never a 0 that stands for « unknown ». A counter the person may not know is absent. They overlap (no total is shown).
export default function CountersBand({ items, state, onRetry, trailing = null }) {
  if (state.status === "error") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-700" role="status" data-testid="counters-error">
        Compteurs indisponibles
        <Button variant="outline" size="sm" onClick={onRetry} data-testid="counters-retry">Réessayer</Button>
        {trailing ? <div className="ml-auto">{trailing}</div> : null}
      </div>
    );
  }
  const loading = state.status === "loading";
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-2" data-testid="counters-row">
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto whitespace-nowrap scrollbar-thin md:flex-wrap md:overflow-visible" role="group" aria-label="À traiter" data-testid="counters-band">
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wider text-slate-600">À traiter</span>
        {items.map((it) => {
          const value = state.data?.[it.id];
          if (!loading && value === undefined) return null;
          return (
            <Tooltip key={it.id}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-pressed={it.active}
                  disabled={loading}
                  onClick={() => it.onClick()}
                  data-testid={`counter-${it.id}`}
                  className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3 h-8 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:h-11 ${
                    it.active ? "border-jamora bg-jamora text-white" : "border-slate-200 bg-white text-slate-700 hover:border-jamora hover:text-jamora"
                  }`}
                >
                  {it.label}
                  <span className={`font-mono ${it.active ? "text-white" : "text-slate-900"}`} data-testid={`counter-value-${it.id}`}>{loading ? "…" : value}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{TIPS[it.id]}</TooltipContent>
            </Tooltip>
          );
        })}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex text-slate-500" tabIndex={0} aria-label="Un prospect peut figurer dans plusieurs compteurs" data-testid="counters-overlap-hint"><Info size={14} /></span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">Un prospect peut figurer dans plusieurs compteurs : leur total n'a pas de sens.</TooltipContent>
        </Tooltip>
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
      </div>
    </TooltipProvider>
  );
}
